// src/services/discountsApi.js
// API сервис для управления скидками

import { API_URL } from '../config';

/**
 * Получение токена из localStorage
 */
const getToken = () => {
  const token = localStorage.getItem('token');
  return token;
};

/**
 * Базовые заголовки для запросов
 */
const getHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

const formatValidationDetail = (detail) => {
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const path = Array.isArray(item?.loc) ? item.loc.slice(1).join('.') : '';
        const message = item?.msg || 'Некорректное значение';
        return path ? `${path}: ${message}` : message;
      })
      .join('\n');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || null;
  }
  return detail || null;
};

/**
 * Обработка ошибок API
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    try {
      const error = JSON.parse(errorText);
      const detail = error.detail;
      if (detail && typeof detail === 'object') {
        const formattedDetail = formatValidationDetail(detail);
        const apiError = new Error(formattedDetail || `Ошибка ${response.status}`);
        apiError.detail = detail;
        apiError.status = response.status;
        throw apiError;
      }
      const apiError = new Error(detail || `Ошибка ${response.status}`);
      apiError.status = response.status;
      throw apiError;
    } catch (e) {
      if (e.detail || e.status) {
        throw e;
      }
      throw new Error(errorText || `Ошибка ${response.status}`);
    }
  }
  return response.json();
};

// ============================================
// Шаблоны скидок (Discount Templates)
// ============================================

/**
 * Получение списка шаблонов скидок
 * @param {Object} params - Параметры запроса
 * @param {string} params.client_type - Фильтр по типу клиента (b2c, b2b)
 * @param {string} params.template_type - Фильтр по типу шаблона
 * @param {boolean} params.active_only - Только активные
 */
export const getDiscountTemplates = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.client_type) queryParams.append('client_type', params.client_type);
  if (params.template_type) queryParams.append('template_type', params.template_type);
  if (params.active_only) queryParams.append('active_only', 'true');

  const response = await fetch(`${API_URL}/api/discount-templates?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение информации о шаблоне скидки
 * @param {number} templateId - ID шаблона
 */
export const getDiscountTemplate = async (templateId) => {
  const response = await fetch(`${API_URL}/api/discount-templates/${templateId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Создание шаблона скидки
 * @param {Object} templateData - Данные шаблона
 */
export const createDiscountTemplate = async (templateData) => {
  const response = await fetch(`${API_URL}/api/discount-templates`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(templateData),
  });
  return handleResponse(response);
};

/**
 * Обновление шаблона скидки
 * @param {number} templateId - ID шаблона
 * @param {Object} templateData - Данные для обновления
 */
export const updateDiscountTemplate = async (templateId, templateData) => {
  const response = await fetch(`${API_URL}/api/discount-templates/${templateId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(templateData),
  });
  return handleResponse(response);
};

/**
 * Удаление шаблона скидки
 * @param {number} templateId - ID шаблона
 */
export const deleteDiscountTemplate = async (templateId) => {
  const response = await fetch(`${API_URL}/api/discount-templates/${templateId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Запланированные скидки (Scheduled Discounts) - УСТАРЕЛО
// ============================================

/**
 * Получение списка запланированных скидок
 * @param {Object} params - Параметры запроса
 * @param {string} params.client_type - Фильтр по типу клиента (b2c, b2b)
 * @param {boolean} params.active_only - Только активные
 * @param {boolean} params.include_expired - Включая истёкшие
 */
export const getScheduledDiscounts = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.client_type) queryParams.append('client_type', params.client_type);
  if (params.active_only) queryParams.append('active_only', 'true');

  const response = await fetch(`${API_URL}/api/discount-templates?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

export const getScheduledDiscount = async (discountId) => {
  const response = await fetch(`${API_URL}/api/discount-templates/${discountId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

export const createScheduledDiscount = async (discountData) => {
  const response = await fetch(`${API_URL}/api/discount-templates`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(discountData),
  });
  return handleResponse(response);
};

export const updateScheduledDiscount = async (discountId, discountData) => {
  const response = await fetch(`${API_URL}/api/discount-templates/${discountId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(discountData),
  });
  return handleResponse(response);
};

export const deleteScheduledDiscount = async (discountId) => {
  const response = await fetch(`${API_URL}/api/discount-templates/${discountId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Акции (Promotions)
// ============================================

/**
 * Получение списка акций
 * @param {Object} params - Параметры запроса
 * @param {string} params.client_type - Фильтр по типу клиента
 * @param {string} params.status - Фильтр по статусу
 * @param {boolean} params.active_only - Только активные сейчас
 */
export const getPromotions = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.client_type) queryParams.append('client_type', params.client_type);
  if (params.status) queryParams.append('status', params.status);
  if (params.active_only) queryParams.append('active_only', 'true');

  const response = await fetch(`${API_URL}/api/discounts/promotions?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение информации об акции
 * @param {number} promoId - ID акции
 */
export const getPromotion = async (promoId) => {
  const response = await fetch(`${API_URL}/api/discounts/promotions/${promoId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Создание акции
 * @param {Object} promotionData - Данные акции
 */
export const createPromotion = async (promotionData) => {
  const response = await fetch(`${API_URL}/api/discounts/promotions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(promotionData),
  });
  return handleResponse(response);
};

/**
 * Обновление акции
 * @param {number} promoId - ID акции
 * @param {Object} promotionData - Данные для обновления
 */
export const updatePromotion = async (promoId, promotionData) => {
  const response = await fetch(`${API_URL}/api/discounts/promotions/${promoId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(promotionData),
  });
  return handleResponse(response);
};

/**
 * Удаление акции
 * @param {number} promoId - ID акции
 */
export const deletePromotion = async (promoId) => {
  const response = await fetch(`${API_URL}/api/discounts/promotions/${promoId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Автоматические скидки (Auto Discounts)
// ============================================

/**
 * Получение списка автоматических скидок
 * @param {Object} params - Параметры запроса
 * @param {string} params.client_type - Фильтр по типу клиента
 * @param {boolean} params.active_only - Только активные
 */
export const getAutoDiscounts = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.client_type) queryParams.append('client_type', params.client_type);
  if (params.active_only) queryParams.append('active_only', 'true');

  const response = await fetch(`${API_URL}/api/discounts/auto?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Создание автоматической скидки
 * @param {Object} discountData - Данные скидки
 */
export const createAutoDiscount = async (discountData) => {
  const response = await fetch(`${API_URL}/api/discounts/auto`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(discountData),
  });
  return handleResponse(response);
};

/**
 * Обновление автоматической скидки
 * @param {number} discountId - ID скидки
 * @param {Object} discountData - Данные для обновления
 */
export const updateAutoDiscount = async (discountId, discountData) => {
  const response = await fetch(`${API_URL}/api/discounts/auto/${discountId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(discountData),
  });
  return handleResponse(response);
};

/**
 * Удаление автоматической скидки
 * @param {number} discountId - ID скидки
 */
export const deleteAutoDiscount = async (discountId) => {
  const response = await fetch(`${API_URL}/api/discounts/auto/${discountId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Расчёт скидок для заказа
// ============================================

/**
 * Расчёт доступных скидок для заказа
 * @param {Object} calculationData - Данные для расчёта
 */
export const calculateDiscounts = async (calculationData) => {
  const response = await fetch(`${API_URL}/api/discounts/calculate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(calculationData),
  });
  return handleResponse(response);
};

// ============================================
// Экспорт всех функций
// ============================================

const discountsApi = {
  // Шаблоны скидок
  getDiscountTemplates,
  getDiscountTemplate,
  createDiscountTemplate,
  updateDiscountTemplate,
  deleteDiscountTemplate,
  
  // Запланированные скидки (устарело)
  getScheduledDiscounts,
  getScheduledDiscount,
  createScheduledDiscount,
  updateScheduledDiscount,
  deleteScheduledDiscount,
  
  // Акции
  getPromotions,
  getPromotion,
  createPromotion,
  updatePromotion,
  deletePromotion,
  
  // Автоматические скидки
  getAutoDiscounts,
  createAutoDiscount,
  updateAutoDiscount,
  deleteAutoDiscount,
  
  // Расчёт
  calculateDiscounts,
};

export default discountsApi;
